/**
 * The stable, cross-feature representation used when a hub resource is shared.
 * Resource pages should adapt their local data to this shape rather than
 * inventing endpoint-specific payloads.
 */
export const RESOURCE_TYPES = [
  "choice-board",
  "picture-schedule",
  "timer",
  "reminder",
  "video-model",
  "social-narrative",
  "location-preparation",
  "reward-chart",
] as const;

export type ResourceType = (typeof RESOURCE_TYPES)[number];

export interface ResourceDescriptor {
  /** The canonical API resource type. */
  type: ResourceType;
  /** Resource IDs vary by feature, so they are normalized to strings. */
  id: string;
  title: string;
  description?: string;
  imageUrl?: string | null;
  tags?: string[];
  /**
   * Resource-specific data that receivers can use to render a richer preview.
   * Keep it JSON-serializable.
   */
  metadata?: Record<string, unknown>;
}

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  "choice-board": "Choice Board",
  "picture-schedule": "Picture Schedule",
  timer: "Timer",
  reminder: "Reminder",
  "video-model": "Video Model",
  "social-narrative": "Social Narrative",
  "location-preparation": "Location Preparation",
  "reward-chart": "Reward Chart",
};

export function resourceShareContent(resource: ResourceDescriptor, message: string): string {
  return message.trim() || `Shared a ${RESOURCE_TYPE_LABELS[resource.type]}: ${resource.title}`;
}

export function communitySharePayload(resource: ResourceDescriptor, message: string) {
  const shareText = message.trim();
  return {
    resourceType: resource.type,
    resourceId: resource.id,
    title: resource.title,
    description: resource.description || "",
    imageUrl: resource.imageUrl || undefined,
    tags: resource.tags || [],
    shareText,
    metadata: resource.metadata || {},
  };
}

export function getPendingShareTargets(
  shareToCommunity: boolean,
  recipientIds: string[],
  communitySucceeded: boolean,
  successfulRecipientIds: string[],
) {
  return {
    community: shareToCommunity && !communitySucceeded,
    recipientIds: recipientIds.filter((id) => !successfulRecipientIds.includes(id)),
  };
}

/**
 * A share dialog may close only after it attempted at least one pending
 * destination and every attempt succeeded.
 */
export function shouldCloseShareDialog(deliveryResults: boolean[]): boolean {
  return deliveryResults.length > 0 && deliveryResults.every(Boolean);
}